"use client";

import Link from "next/link";
import { useState, useTransition, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import {
  ArrowRightLeft,
  ChevronDown,
  MoreHorizontal,
  VolumeX,
  X,
} from "lucide-react";
import { ScoreChip } from "@/app/_components/verdict/ScoreChip";
import { setPlaceBase } from "@/app/actions/companion-bases";
import { removeFavourite } from "@/app/actions/favourites";
import { unfollowTheCall } from "@/app/actions/the-call";
import {
  companionPlaceOperationMessage as planPlaceOperationMessage,
  settleCompanionPlaceOperations as settlePlanPlaceOperations,
  type CompanionPlaceOperationBatchResult as PlanPlaceOperationBatchResult,
} from "@/lib/companion/place-operations";
import type { CompanionHomeModel } from "@/lib/companion/home-model";
import { CompanionAvailabilityForm } from "../CompanionAvailabilityForm";
import { CompanionDayIntentControls } from "../CompanionDayIntentControls";
import {
  buildCompanionPlaceGroupsView,
  buildCompanionWeekPreview,
  buildCompanionWeekSummary,
  buildCompanionZoneAState,
  resolvePrimaryHomeBaseId,
  type CompanionPlaceRowView,
} from "../companion-home-state";
import { companionWeekCalendarLayout } from "../companion-week-layout";
import type { CompanionWorkspaceProps } from "./companion-workspace-types";

export function CompanionPlanWorkspace({
  data,
  model,
  renderedAt,
  showIntentControls = true,
  showAvailability = false,
}: CompanionWorkspaceProps & {
  showIntentControls?: boolean;
  showAvailability?: boolean;
}): ReactNode {
  const zone = buildCompanionZoneAState(data, model, { now: new Date(renderedAt) });
  return (
    <div className="space-y-5">
      {showAvailability ? (
        <CompanionAvailabilityForm
          windows={data.availabilityManagementWindows}
          fitContext={zone.availabilityFitContext}
          renderedAt={renderedAt}
        />
      ) : null}
      <PlaceComparisonSection model={model} renderedAt={renderedAt} />
      <WeekCalendarSection model={model} showIntentControls={showIntentControls} />
    </div>
  );
}

function PlaceComparisonSection({
  model,
  renderedAt,
}: {
  model: CompanionHomeModel;
  renderedAt: string;
}) {
  const places = model.mountainOrCoast;
  const hasMultiplePlaces = places.length > 1;
  const groups = buildCompanionPlaceGroupsView(model, {
    now: new Date(renderedAt),
  });
  const primaryHomeBaseId = resolvePrimaryHomeBaseId(model.bases);

  return (
    <section aria-labelledby="places-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
            Saved places
          </p>
          <h2 id="places-heading" className="font-serif text-[28px] leading-tight text-navy">
            Compare the day before you commit
          </h2>
          <p className="mt-2 max-w-[56ch] text-[13.5px] leading-relaxed text-text-muted">
            {hasMultiplePlaces
              ? "Keep mountains, coasts and festival stops side by side so the best day can win."
              : "Keep a holiday stop, season base or future trip ready without making it your only place."}
          </p>
        </div>
        <Link
          href="/conditions"
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-sm border border-border bg-white px-4 py-2 text-[13px] font-semibold text-navy no-underline hover:border-gold/50"
        >
          Conditions
        </Link>
      </div>

      {groups.length > 0 ? (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.key} className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[14px] font-semibold text-navy">{group.heading}</h3>
                  {group.tripEndedState ? (
                    <span className="inline-flex min-h-[28px] items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-950">
                      {group.tripEndedState.tagLabel}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                  <span className="text-[12px] font-semibold text-text-muted">
                    {group.places.length}
                  </span>
                  {group.tripEndedState ? (
                    <EndedTripGroupActions
                      state={group.tripEndedState}
                      places={group.places}
                      keepFollowingBaseId={primaryHomeBaseId}
                    />
                  ) : null}
                  <Link
                    href={group.addPlaceHref}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
                  >
                    {group.addPlaceLabel}
                  </Link>
                </div>
              </div>
              <div className="space-y-3">
                {group.places.map((place, index) => (
                  <PlaceRow
                    key={place.id}
                    place={place}
                    isLead={index === 0 && place.id === model.visiblePlace?.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <SetupTile
            href="/conditions/piste"
            title="Mountain plans"
            body="Start with piste conditions and save a resort for planner shortcuts."
          />
          <SetupTile
            href="/conditions/surf"
            title="Coastal sessions"
            body="Find the best window before you travel."
          />
          <SetupTile
            href="/conditions"
            title="Save to Companion"
            body="Use conditions pages to save places into this home."
          />
        </div>
      )}
    </section>
  );
}

function PlaceRow({
  place,
  isLead,
}: {
  place: CompanionPlaceRowView;
  isLead: boolean;
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [status, setStatus] = useState<{ tone: "success" | "error"; message: string } | null>(
    null,
  );

  function handleMove(target: NonNullable<CompanionPlaceRowView["actionState"]>["moveTargets"][number]) {
    if (!place.actionState) return;

    startTransition(async () => {
      const result = await setPlaceBase(place.actionState!.favouriteId, target.baseId);
      if (!result.success) {
        setStatus({ tone: "error", message: result.error ?? "Could not move this place right now." });
        return;
      }

      setStatus({
        tone: "success",
        message:
          target.baseId === null
            ? "Moved to Elsewhere."
            : `Moved to ${target.label.replace(/^Move to /, "")}.`,
      });
      queueCompanionRefresh(router);
    });
  }

  function handleRemove() {
    if (!place.actionState?.canRemove || !place.actionState.spotSlug) return;

    startTransition(async () => {
      const result = await removeFavourite(place.actionState!.spotSlug!);
      if (!result.success) {
        setStatus({
          tone: "error",
          message: result.error ?? "Could not remove this place right now.",
        });
        return;
      }

      setStatus({ tone: "success", message: "Removed from Companion." });
      queueCompanionRefresh(router);
    });
  }

  return (
    <article className="rounded-md border border-border-light bg-warm-bg p-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold leading-tight text-navy">{place.name}</h3>
            {isLead ? (
              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-950">
                Best saved signal
              </span>
            ) : null}
            {place.staleness ? (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-950">
                {place.staleness.label}
              </span>
            ) : null}
            {place.confirmedLabel ? (
              <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-950">
                {place.confirmedLabel}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[12px] text-text-muted">
            {place.region ?? "Saved place"}
          </p>
          {place.kind === "condition" ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {place.sportChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex min-h-[32px] items-center rounded-full border border-border bg-white px-3 py-1 text-[12px] font-semibold text-navy"
                >
                  {chip.label}
                </span>
              ))}
              {place.followSportChip ? (
                <Link
                  href={place.followSportChip.href}
                  className="inline-flex min-h-[44px] items-center rounded-full border border-dashed border-gold bg-white px-3 py-1 text-[12px] font-semibold text-gold no-underline hover:border-navy hover:text-navy"
                >
                  {place.followSportChip.label}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
        {place.showScoreChip ? (
          <div className="shrink-0">
            <ScoreChip score={place.score} />
          </div>
        ) : null}
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-text-muted">
        {place.verdict ?? "Open conditions for the latest context."}
      </p>
      {place.windowLine ? (
        <p className="mt-2 text-[12.5px] font-semibold text-navy">
          {place.windowLine}
        </p>
      ) : null}
      <p className="mt-3 rounded-sm bg-white px-3 py-2 text-[12px] leading-relaxed text-text-muted">
        <span className="font-semibold text-navy">{place.disclosure.label}.</span>{" "}
        {place.disclosure.body}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          href={place.href}
          className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white no-underline hover:bg-navy/90"
        >
          Open
        </Link>
        {place.plannerHref ? (
          <Link
            href={place.plannerHref}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
          >
            Planner
          </Link>
        ) : null}
      </div>
      {place.actionState ? (
        <details className="mt-4 rounded-sm border border-border bg-white">
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-[12px] font-semibold text-navy">
            <span className="inline-flex items-center gap-2">
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              Actions
            </span>
            <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden="true" />
          </summary>
          <div className="border-t border-border-light px-3 py-3">
            {place.actionState.moveTargets.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {place.actionState.moveTargets.map((target) => (
                  <button
                    key={target.baseId ?? "elsewhere"}
                    type="button"
                    onClick={() => handleMove(target)}
                    disabled={pending}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />
                    {target.label}
                  </button>
                ))}
              </div>
            ) : null}
            {place.actionState.canRemove ? (
              <div className={place.actionState.moveTargets.length > 0 ? "mt-2" : ""}>
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={pending}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-900 transition-colors hover:border-rose-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Remove
                </button>
              </div>
            ) : null}
            {status ? (
              <p
                className={`mt-3 text-[12px] leading-relaxed ${
                  status.tone === "error" ? "text-rose-700" : "text-emerald-700"
                }`}
              >
                {status.message}
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </article>
  );
}

function EndedTripGroupActions({
  state,
  places,
  keepFollowingBaseId,
}: {
  state: NonNullable<ReturnType<typeof buildCompanionPlaceGroupsView>[number]["tripEndedState"]>;
  places: readonly CompanionPlaceRowView[];
  keepFollowingBaseId: string | null;
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [confirmingMute, setConfirmingMute] = useState(false);
  const [status, setStatus] = useState<{ tone: "success" | "error"; message: string } | null>(
    null,
  );
  const [operationResult, setOperationResult] = useState<PlanPlaceOperationBatchResult | null>(null);

  function handleKeepFollowing() {
    startTransition(async () => {
      const result = await settlePlanPlaceOperations(
        state.movePlaceIds.map((favouriteId, index) => {
          const place = places.find(
            (candidate) => candidate.actionState?.favouriteId === favouriteId,
          );
          return {
            placeId: favouriteId,
            placeName: place?.name ?? `Saved place ${index + 1}`,
            actionId: "move",
            run: () => setPlaceBase(favouriteId, keepFollowingBaseId),
          };
        }),
      );

      setOperationResult(result);
      setStatus({
        tone: result.outcome === "success" ? "success" : "error",
        message: planPlaceOperationMessage(
          result,
          keepFollowingBaseId === null
            ? "moved to Elsewhere"
            : "moved to your home base",
        ),
      });
      if (result.places.some((place) => place.succeeded > 0)) {
        queueCompanionRefresh(router);
      }
    });
  }

  function handleMute() {
    startTransition(async () => {
      const result = await settlePlanPlaceOperations(
        state.muteTargets.map((target) => {
          const place = places.find(
            (candidate) => candidate.actionState?.spotSlug === target.spotSlug,
          );
          return {
            placeId: target.spotSlug,
            placeName: place?.name ?? target.spotSlug,
            actionId: `mute ${target.sport}`,
            run: () => unfollowTheCall(target.spotSlug, target.sport),
          };
        }),
      );

      setConfirmingMute(false);
      setOperationResult(result);
      setStatus({
        tone: result.outcome === "success" ? "success" : "error",
        message: planPlaceOperationMessage(result, "muted"),
      });
      if (result.places.some((place) => place.succeeded > 0)) {
        queueCompanionRefresh(router);
      }
    });
  }

  return (
    <div className="flex flex-col items-stretch gap-2 sm:items-end">
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={handleKeepFollowing}
          disabled={pending}
          className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {state.keepFollowingLabel}
        </button>
        <button
          type="button"
          onClick={() => setConfirmingMute((value) => !value)}
          disabled={pending}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <VolumeX className="h-4 w-4" aria-hidden="true" />
          {state.muteAlertsLabel}
        </button>
      </div>
      {confirmingMute ? (
        <div className="w-full max-w-[420px] rounded-sm border border-amber-200 bg-amber-50 px-3 py-3">
          <p className="text-[12px] leading-relaxed text-amber-950">{state.confirmCopy}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleMute}
              disabled={pending}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setConfirmingMute(false)}
              disabled={pending}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      <div aria-live="polite" aria-atomic="true" className="w-full max-w-[420px]">
        {status ? (
          <p
            className={`text-[12px] leading-relaxed ${
              status.tone === "error" ? "text-rose-700" : "text-emerald-700"
            }`}
          >
            {status.message}
          </p>
        ) : null}
        {operationResult ? <PlanOperationResults result={operationResult} /> : null}
      </div>
    </div>
  );
}

function PlanOperationResults({ result }: { result: PlanPlaceOperationBatchResult }) {
  if (result.places.length === 0) return null;

  return (
    <ul className="mt-1 space-y-1 text-[11px] leading-relaxed text-text-muted">
      {result.places.map((place) => (
        <li key={place.placeId}>
          <span className="font-semibold text-navy">{place.placeName}:</span>{" "}
          {place.outcome === "success"
            ? "complete"
            : place.outcome === "partial"
              ? `partly complete (${place.failures.join(", ")})`
              : `not completed (${place.failures.join(", ")})`}
        </li>
      ))}
    </ul>
  );
}

function WeekCalendarSection({
  model,
  showIntentControls,
}: {
  model: CompanionHomeModel;
  showIntentControls: boolean;
}) {
  const week = buildCompanionWeekPreview(model);
  const summary = buildCompanionWeekSummary(model);

  return (
    <section aria-labelledby="local-week-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
            This week
          </p>
          <h2 id="local-week-heading" className="font-serif text-[28px] leading-tight text-navy">
            This week
          </h2>
          <p className="mt-2 max-w-[60ch] text-[13.5px] leading-relaxed text-text-muted">
            {summary}
          </p>
        </div>
      </div>

      <div className={companionWeekCalendarLayout.rail}>
        {week.map((day) => (
          <article
            key={day.id}
            aria-label={`Plan for ${day.dayLabel}, ${day.dateLabel}`}
            className={companionWeekCalendarLayout.card}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-navy">{day.dayLabel}</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[1.1px] text-text-muted">
                  {day.dateLabel}
                </p>
              </div>
              <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-[1px] ${weekToneClass(day.conditionTone)}`}>
                {day.conditionLabel}
              </span>
            </div>

            <div className="mt-3 rounded-sm bg-white px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[1.1px] text-text-muted">
                Conditions
              </p>
              {day.placeName ? (
                <Link
                  href={day.placeHref}
                  className="mt-1 inline-flex min-h-[44px] items-center text-[13px] font-semibold leading-snug text-navy no-underline hover:text-gold"
                >
                  {day.placeName}
                </Link>
              ) : (
                <p className="mt-1 text-[13px] font-semibold leading-snug text-navy">
                  No place lined up
                </p>
              )}
              <p className="mt-2 text-[12px] leading-relaxed text-text-muted">
                {day.conditionDetail}
              </p>
            </div>

            {showIntentControls && day.placeSlug ? (
              <CompanionDayIntentControls
                date={day.id}
                placeSlug={day.placeSlug}
                placeName={day.placeName}
                summary={day.intentSummary}
              />
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function weekToneClass(
  tone: ReturnType<typeof buildCompanionWeekPreview>[number]["conditionTone"],
): string {
  const classes: Record<
    ReturnType<typeof buildCompanionWeekPreview>[number]["conditionTone"],
    string
  > = {
    strong: "bg-emerald-100 text-emerald-950",
    useful: "bg-gold/20 text-navy",
    weak: "bg-slate-200 text-slate-800",
    unknown: "bg-white text-text-muted",
  };
  return classes[tone];
}

export function SetupTile({
  href,
  title,
  body,
}: {
  href: string;
  title: string;
  body: string;
}) {
  return (
    <Link
      href={href}
      className="block rounded-md border border-dashed border-border bg-white/85 p-4 no-underline hover:border-gold/50"
    >
      <span className="block text-[14px] font-semibold text-navy">{title}</span>
      <span className="mt-1 block text-[12.5px] leading-relaxed text-text-muted">{body}</span>
    </Link>
  );
}

export function queueCompanionRefresh(router: ReturnType<typeof useRouter>) {
  window.setTimeout(() => {
    router.refresh();
  }, 700);
}
